import { Badge, Box, SxProps, Typography } from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BROKERS_LOGOS_BASE_URL } from '../../assets/constants'
import PaperTradingLogo from '../../assets/icons/PaperTradingLogo'
import { selectBrokers, selectPrimaryTradingAccount, selectTradingAccounts } from '../../selectors/user'
import { BrokerId, generateBrokerLoginUrl, keyValueArrayFromTradingAccountParams } from '../../util/tradingAccounts'
import { ExtraSmallButton, InteractiveTooltip } from '../custom'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import { TradingAccount, TradingAccountStatus } from '../../proto/generated/trading-bridge'
import { setPrimaryTradingAccountApi } from '../../services/apis/tradingAccount'
import ROUTES from '../../assets/routes'
import Add from '../../assets/icons/Add'
import { useNavigate } from 'react-router-dom'

type Props = {
    compact?: boolean,
    disabled?: boolean,
    defaultTradingAccountId?: string
    fullWidth?: boolean
    sx?: SxProps
    closeQuickTradeWindow?: () => void
}

const TradingAccountSelector = ({ compact = false, disabled = false, defaultTradingAccountId = '', fullWidth = false, sx = {}, closeQuickTradeWindow }: Props) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const tradingAccounts = useSelector(selectTradingAccounts)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const widgetTradingAccount = useMemo(() => {
        if (disabled) {
            return tradingAccounts.find(t => t.accountId === defaultTradingAccountId) ?? primaryTradingAccount
        }
        return primaryTradingAccount
    }, [disabled, primaryTradingAccount, tradingAccounts, defaultTradingAccountId])
    const brokers = useSelector(selectBrokers)
    const open = Boolean(anchorEl)
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = (closeWindow: boolean = false) => {
        setAnchorEl(null)
        if (closeWindow && closeQuickTradeWindow) {
            closeQuickTradeWindow()
        }
    }

    const brokerImageUrlFromCode = useCallback((code: string) => {
        const broker = brokers.find(b => b.code === code)
        return broker ? BROKERS_LOGOS_BASE_URL + broker.imageUrl : ''
    }, [brokers])

    const handleConnectBrokenAccount = useCallback(async (tradingAccount: TradingAccount) => {
        if (tradingAccount && tradingAccount.status !== TradingAccountStatus.ACTIVE) {
            if (tradingAccount.brokerCode !== BrokerId.DHAN) {
                const fetchedUrl = await generateBrokerLoginUrl(tradingAccount.brokerCode, keyValueArrayFromTradingAccountParams(tradingAccount.tradingAccountParameters))
                window.open(fetchedUrl, '_self')
            } else {
                // setDeveloperAccountCreationRequest(tradingAccount.brokerCode)
            }
        }
    }, [brokers])

    const changePrimaryTradingAccount = useCallback(
        async (account: TradingAccount) => {
            if (account.brokerAccountId !== primaryTradingAccount?.brokerAccountId) {
                try {
                    await setPrimaryTradingAccountApi({
                        body: {
                            tradingAccountId: account.accountId
                        }
                    })
                } catch (e) {
                    console.error(e, 'error in change primary account')
                }
            }
            if (account.status === TradingAccountStatus.BROKEN) {
                handleConnectBrokenAccount(account)
            }
            // dispatch(closeConnectAccountDialog())
        },
        [dispatch, handleConnectBrokenAccount, primaryTradingAccount]
    )

    return (
        <span style={fullWidth ? { width: '100%' } : {}}>
            {
                compact ?
                    (
                        tradingAccounts?.length === 0 ?
                            <InteractiveTooltip title={"Connect Broker"} arrow placement='left'>
                                <span style={{ display: 'inline-flex' }}>
                                    <ExtraSmallButton
                                        variant='outlined'
                                        size="small"
                                        onClick={()=>{
                                            navigate(ROUTES.tradingAccount)
                                            handleClose(true)
                                        }}
                                        disabled={disabled}
                                        sx={{ ...sx, width: '32px' }}
                                    >
                                        <Add height={40} width={40} />
                                    </ExtraSmallButton>
                                </span>
                            </InteractiveTooltip> :
                            <InteractiveTooltip title={widgetTradingAccount?.displayName} arrow placement='left'>
                                <span style={{ display: 'inline-flex' }}>
                                    <ExtraSmallButton
                                        variant='outlined'
                                        size="small"
                                        onClick={handleClick}
                                        disabled={disabled}
                                        sx={{ ...sx }}
                                    >
                                        {widgetTradingAccount && widgetTradingAccount?.brokerCode === BrokerId.PAPER_TRADING ? (
                                            <PaperTradingLogo sx={{ fontSize: '25px' }} />
                                        ) : widgetTradingAccount ? (
                                            <Box
                                                component="img"
                                                sx={{ height: '25px', width: '25px', borderRadius: widgetTradingAccount && widgetTradingAccount?.brokerCode === BrokerId.DHAN ? '100%' : '0%' }}
                                                src={widgetTradingAccount && widgetTradingAccount?.brokerCode ? brokerImageUrlFromCode(widgetTradingAccount?.brokerCode) : undefined}
                                                alt="broker"
                                            />
                                        ) : '-'}
                                    </ExtraSmallButton>
                                </span>
                            </InteractiveTooltip>
                    ) : (
                        tradingAccounts?.length === 0 ?
                            <ExtraSmallButton variant='outlined' onClick={() => {
                                navigate(ROUTES.tradingAccount)
                                handleClose(true)
                            }}>
                                <Add height={30} width={30} />
                                <Typography variant="body2" noWrap fontSize={'14px'} sx={{ flexGrow: 1 }}>
                                    Connect Broker
                                </Typography>
                            </ExtraSmallButton> :
                            <ExtraSmallButton
                                variant='outlined'
                                size="small"
                                onClick={handleClick}
                                disabled={disabled}
                                endIcon={<ThinArrowDownIcon />}
                                sx={fullWidth ? { width: '100%' } : {}}
                            >
                                {
                                    widgetTradingAccount && widgetTradingAccount?.brokerCode === BrokerId.PAPER_TRADING ? (
                                        <PaperTradingLogo sx={{ fontSize: '25px', marginRight: "5px" }} />
                                    ) : widgetTradingAccount ? (
                                        <Box
                                            component="img"
                                            sx={{ height: '25px', width: '25px', marginRight: "5px", borderRadius: widgetTradingAccount && widgetTradingAccount?.brokerCode === BrokerId.DHAN ? '100%' : '0%' }}
                                            src={widgetTradingAccount && widgetTradingAccount?.brokerCode ? brokerImageUrlFromCode(widgetTradingAccount?.brokerCode) : undefined}
                                            alt="broker"
                                        />
                                    ) : '-'
                                }
                            </ExtraSmallButton>
                    )
            }
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                slotProps={{
                    paper: {
                        sx: {
                            width: anchorEl?.clientWidth ?? 0,
                            minWidth: '170px',
                            '& .MuiList-root': {
                                padding: 0
                            }
                        }
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                {[...(tradingAccounts ?? [])]
                    .sort((a, b) => {
                        if (a.primary && !b.primary) return -1
                        if (!a.primary && b.primary) return 1
                        return 0
                    }).map((account) => {
                        return (
                            <MenuItem
                                selected={account?.accountId === widgetTradingAccount?.accountId}
                                key={account.accountId}
                                onClick={() => {
                                    changePrimaryTradingAccount(account)
                                    handleClose()
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1,
                                }}
                            >
                                {account && account.brokerCode === BrokerId.PAPER_TRADING ? (
                                    <PaperTradingLogo sx={{ fontSize: '25px', marginRight: "5px" }} />
                                ) : (

                                    <Box
                                        component="img"
                                        sx={{ height: '25px', width: '25px', marginRight: "5px", borderRadius: account && account.brokerCode === BrokerId.DHAN ? '100%' : '0%' }}
                                        src={account && account.brokerCode ? brokerImageUrlFromCode(account.brokerCode) : undefined}
                                        alt="broker"
                                    />

                                )}
                                <Typography variant="body2" noWrap fontSize={'14px'} sx={{ flexGrow: 1 }}>
                                    {account.displayName}
                                </Typography>
                                <Badge sx={{ mr: 1 }} color={account.status !== TradingAccountStatus.ACTIVE ? 'error' : "success"} variant="dot" overlap="circular" ></Badge>
                            </MenuItem>
                        )
                    })}
                <MenuItem
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        height: '40px'
                    }}
                    onClick={() => {
                        navigate(ROUTES.tradingAccount)
                        handleClose(true)
                    }}
                >
                    <Add height={30} width={30} />
                    <Typography variant="body2" noWrap fontSize={'14px'} sx={{ flexGrow: 1 }}>
                        Connect Broker
                    </Typography>
                </MenuItem>
            </Menu>
        </span>
    )
}

export default TradingAccountSelector